import React, { useState } from "react";

import { useBetslipStore } from "@/features/betslip/stores/betslipStore";
import { useAcceptedOdds } from "@/features/betslip/hooks/useAcceptedOdds";
import { useSetSingleStake } from "@/features/betslip/hooks/useStakeActions";
import { useRemainingPromoBalance } from "@/features/betslip/hooks/useRemainingPromoBalance";
import type {
  MatchSelection,
  OutrightSelection,
  RaceSelection,
  SemSelection,
} from "@/features/betslip/types";
import { StakeInput } from "@/features/betslip/components/parts/StakeInput";
import { OddsDisplay } from "@/features/betslip/components/parts/OddsDisplay";
import { PayoutValue } from "@/features/betslip/components/parts/PayoutValue";
import { StakeStrip } from "@/features/betslip/components/parts/StakeStrip";
import { QuickStakeChips } from "@/features/betslip/components/parts/QuickStakeChips";

type SingleFooterSelection =
  | MatchSelection
  | OutrightSelection
  | RaceSelection
  | SemSelection;

type SingleFooterProps = {
  outcomeId: string;
  selection: SingleFooterSelection;
};

export const SingleFooter = ({ outcomeId, selection }: SingleFooterProps) => {
  // Display stake and payout are partial-acceptance aware in the store,
  // so the card just renders them.
  const stake = useBetslipStore((s) => s.singleDisplayStakes[outcomeId] ?? 0);
  const pendingOdds = useBetslipStore((s) => s.oddsChanges[outcomeId]);
  const acceptedOdds = useAcceptedOdds(outcomeId);
  const isUsingPromo = useBetslipStore(
    (s) => s.promoCash.singles[outcomeId] ?? false,
  );

  const payout = useBetslipStore((s) => s.singlePayouts[outcomeId] ?? 0);
  const isStakeDisabled = useBetslipStore(
    (s) => s.stakeDisabled[outcomeId] ?? false,
  );
  const isLiveViewOnly = useBetslipStore((s) => s.liveViewOnly[outcomeId]);
  const setSingleStake = useSetSingleStake();
  const maxPromoStakeCents = useRemainingPromoBalance({
    type: "single",
    outcomeId,
  });
  const [hasStakeFocus, setHasStakeFocus] = useState(false);

  return (
    <StakeStrip
      testId={`stake-strip-${outcomeId}`}
      promoActive={isUsingPromo}
      leftSlot={
        <StakeInput
          value={stake}
          onChange={(cents) => setSingleStake(outcomeId, cents)}
          disabled={isStakeDisabled}
          testId={`stake-input-${outcomeId}`}
          onFocus={() => setHasStakeFocus(true)}
          onBlur={() => setHasStakeFocus(false)}
        />
      }
      middleSlot={
        <OddsDisplay
          odds={acceptedOdds ?? selection.odds}
          dash={isLiveViewOnly}
          newOdds={pendingOdds}
          sp={selection.isSP}
          showPreviousOdds
        />
      }
      rightSlot={
        <PayoutValue
          dollars={payout}
          tbd={selection.isSP}
          testId={`payout-${outcomeId}`}
        />
      }
      quickStake={
        <QuickStakeChips
          isOpen={hasStakeFocus && !isStakeDisabled}
          valueCents={stake}
          onSetCents={(cents) => setSingleStake(outcomeId, cents)}
          isPromo={isUsingPromo}
          maxPromoStakeCents={maxPromoStakeCents}
          arrowOffset="calc(-50%)"
          detached
        />
      }
    />
  );
};
